import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Download, Heart, MessageCircle, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import { ImagePost, Post } from "../../../types";
import { useAuth, useMessage } from "../../../global-components/src";
import { useNavigate } from "react-router-dom";
import { getImageUser, validatePrev } from "../../../global-components/src/utils/auth";
import { errorMessage } from "../../../utils/TextManagerTwo";
import axios from "axios";
import "./image-crop-modal.css"

interface ImageModalProps {
    src?: string;
    images?: ImagePost[];
    initialIndex?: number;
    alt?: string;
    post?: Post;
    open: boolean;
    onClose: () => void;
    username?: string;
    timeAgo?: string;
    img_account?: string;
    likes?: number;
    comments?: number;
    onLike?: () => void;
    onComment?: () => void;
    onShare?: (src: string) => void | Promise<void>;
    setPost?: React.Dispatch<React.SetStateAction<Post | null>>;
    setPosts?: React.Dispatch<React.SetStateAction<Post[]>>;
    isLiked?: boolean;
    getImagePath?: (path: string) => string;
}

const SWIPE_THRESHOLD = 50; // px mínimo para trocar de imagem
const SWIPE_RUBBER = 0.3;   // resistência ao puxar além do limite (0-1)

const ImageModal: React.FC<ImageModalProps> = ({
    src,
    images,
    initialIndex = 0,
    alt = "Imagem da publicação",
    open,
    onClose,
    username,
    timeAgo,
    likes = 0,
    comments = 0,
    onLike,
    onComment,
    onShare,
    setPost,
    setPosts,
    isLiked = false,
    post,
    img_account,
    getImagePath,
}) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null); // container do carrossel
    const [barsVisible, setBarsVisible] = useState(true);
    const [scale, setScale] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const scaleRef = useRef(1);
    const translateRef = useRef({ x: 0, y: 0 });
    const isDraggingRef = useRef(false);
    const lastPointerRef = useRef({ x: 0, y: 0 });
    const lastPinchDistRef = useRef<number | null>(null);
    const { isAuthenticated, user, token } = useAuth();
    const navigate = useNavigate();
    const { setMessage } = useMessage();

    // --- Carrossel ---
    const imageList = images?.filter(img => img.type === 0) ?? [];
    const hasMultiple = imageList.length > 1;
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    // swipe state
    const swipeStartX = useRef<number | null>(null);
    const swipeDeltaX = useRef(0);
    const [swipeOffset, setSwipeOffset] = useState(0); // px offset visual durante drag

    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
            setSwipeOffset(0);
        }
    }, [open, initialIndex]);

    const currentSrc = imageList.length > 0
        ? (getImagePath ? getImagePath(imageList[currentIndex]?.path ?? "") : imageList[currentIndex]?.path ?? "")
        : (src ?? "");

    const getSrc = (index: number) =>
        getImagePath ? getImagePath(imageList[index]?.path ?? "") : imageList[index]?.path ?? "";

    const goTo = useCallback((index: number) => {
        resetZoom();
        setSwipeOffset(0);
        setCurrentIndex(index);
    }, []);

    const goPrev = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (currentIndex > 0) goTo(currentIndex - 1);
    }, [currentIndex, goTo]);

    const goNext = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (currentIndex < imageList.length - 1) goTo(currentIndex + 1);
    }, [currentIndex, imageList.length, goTo]);

    // Teclado
    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (hasMultiple && e.key === "ArrowRight") goTo(Math.min(currentIndex + 1, imageList.length - 1));
            if (hasMultiple && e.key === "ArrowLeft") goTo(Math.max(currentIndex - 1, 0));
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [open, onClose, currentIndex, hasMultiple, imageList.length, goTo]);

    // --- Zoom ---
    const resetZoom = () => {
        scaleRef.current = 1;
        translateRef.current = { x: 0, y: 0 };
        setScale(1);
        setTranslate({ x: 0, y: 0 });
    };

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    useEffect(() => {
        if (!open) {
            resetZoom();
            setBarsVisible(true);
            setSwipeOffset(0);
        }
    }, [open]);

    const applyTransform = useCallback((animated = false) => {
        if (!imgRef.current) return;
        imgRef.current.style.transition = animated ? "transform 0.2s ease" : "none";
        imgRef.current.style.transform = `scale(${scaleRef.current}) translate(${translateRef.current.x / scaleRef.current}px, ${translateRef.current.y / scaleRef.current}px)`;
        setScale(scaleRef.current);
        setTranslate({ ...translateRef.current });
    }, []);

    const clampTranslate = useCallback(() => {
        const img = imgRef.current;
        const wrapper = wrapperRef.current;
        if (!img || !wrapper) return;
        const imgW = img.offsetWidth * scaleRef.current;
        const imgH = img.offsetHeight * scaleRef.current;
        const maxX = Math.max(0, (imgW - wrapper.offsetWidth) / 2);
        const maxY = Math.max(0, (imgH - wrapper.offsetHeight) / 2);
        translateRef.current.x = Math.max(-maxX, Math.min(maxX, translateRef.current.x));
        translateRef.current.y = Math.max(-maxY, Math.min(maxY, translateRef.current.y));
    }, []);

    // Wheel zoom
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.15 : 0.15;
            scaleRef.current = Math.max(1, Math.min(4, scaleRef.current + delta));
            if (scaleRef.current === 1) translateRef.current = { x: 0, y: 0 };
            clampTranslate();
            applyTransform(false);
        };
        wrapper.addEventListener("wheel", handleWheel, { passive: false });
        return () => wrapper.removeEventListener("wheel", handleWheel);
    }, [open, applyTransform, clampTranslate]);

    // ─── TOUCH: pinch-zoom + pan (com zoom) + swipe (sem zoom) ───────────────
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                // pinch
                swipeStartX.current = null;
                const t1 = e.touches[0], t2 = e.touches[1];
                lastPinchDistRef.current = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
            } else if (e.touches.length === 1) {
                if (scaleRef.current > 1) {
                    // pan com zoom ativo
                    swipeStartX.current = null;
                    lastPointerRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                } else if (hasMultiple) {
                    // início de swipe
                    swipeStartX.current = e.touches[0].clientX;
                    swipeDeltaX.current = 0;
                }
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                // pinch-zoom
                e.preventDefault();
                const t1 = e.touches[0], t2 = e.touches[1];
                const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                if (lastPinchDistRef.current) {
                    scaleRef.current = Math.max(1, Math.min(4, scaleRef.current * (dist / lastPinchDistRef.current)));
                }
                lastPinchDistRef.current = dist;
                if (scaleRef.current === 1) translateRef.current = { x: 0, y: 0 };
                clampTranslate();
                applyTransform(false);
            } else if (e.touches.length === 1) {
                if (scaleRef.current > 1) {
                    // pan
                    translateRef.current.x += e.touches[0].clientX - lastPointerRef.current.x;
                    translateRef.current.y += e.touches[0].clientY - lastPointerRef.current.y;
                    lastPointerRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                    clampTranslate();
                    applyTransform(false);
                } else if (hasMultiple && swipeStartX.current !== null) {
                    // swipe carrossel
                    const dx = e.touches[0].clientX - swipeStartX.current;
                    swipeDeltaX.current = dx;

                    // rubber band nas bordas
                    const atStart = currentIndex === 0 && dx > 0;
                    const atEnd = currentIndex === imageList.length - 1 && dx < 0;
                    const offset = (atStart || atEnd) ? dx * SWIPE_RUBBER : dx;
                    setSwipeOffset(offset);
                    isDraggingRef.current = Math.abs(dx) > 5;
                }
            }
        };

        const onTouchEnd = () => {
            lastPinchDistRef.current = null;

            if (hasMultiple && swipeStartX.current !== null) {
                const dx = swipeDeltaX.current;
                if (dx < -SWIPE_THRESHOLD && currentIndex < imageList.length - 1) {
                    goTo(currentIndex + 1);
                } else if (dx > SWIPE_THRESHOLD && currentIndex > 0) {
                    goTo(currentIndex - 1);
                } else {
                    // volta pro lugar com spring
                    setSwipeOffset(0);
                }
                swipeStartX.current = null;
                swipeDeltaX.current = 0;
                setTimeout(() => { isDraggingRef.current = false; }, 50);
            }
        };

        wrapper.addEventListener("touchstart", onTouchStart, { passive: true });
        wrapper.addEventListener("touchmove", onTouchMove, { passive: false });
        wrapper.addEventListener("touchend", onTouchEnd);
        return () => {
            wrapper.removeEventListener("touchstart", onTouchStart);
            wrapper.removeEventListener("touchmove", onTouchMove);
            wrapper.removeEventListener("touchend", onTouchEnd);
        };
    }, [open, applyTransform, clampTranslate, currentIndex, hasMultiple, imageList.length, goTo]);

    // ─── MOUSE drag: zoom pan  OU  swipe carrossel ────────────────────────────
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            isDraggingRef.current = false;
            lastPointerRef.current = { x: e.clientX, y: e.clientY };
            swipeDeltaX.current = 0;

            const onMove = (ev: MouseEvent) => {
                const dx = ev.clientX - lastPointerRef.current.x;
                const dy = ev.clientY - lastPointerRef.current.y;

                if (scaleRef.current > 1) {
                    // pan com zoom
                    isDraggingRef.current = true;
                    translateRef.current.x += ev.clientX - lastPointerRef.current.x;
                    translateRef.current.y += ev.clientY - lastPointerRef.current.y;
                    lastPointerRef.current = { x: ev.clientX, y: ev.clientY };
                    clampTranslate();
                    applyTransform(false);
                } else if (hasMultiple) {
                    // swipe mouse
                    const totalDx = ev.clientX - (lastPointerRef.current.x - swipeDeltaX.current); // acumulado
                    swipeDeltaX.current = ev.clientX - lastPointerRef.current.x;

                    const accumulated = ev.clientX - (e.clientX); // total desde mousedown
                    if (Math.abs(accumulated) > 5) isDraggingRef.current = true;

                    const atStart = currentIndex === 0 && accumulated > 0;
                    const atEnd = currentIndex === imageList.length - 1 && accumulated < 0;
                    const offset = (atStart || atEnd) ? accumulated * SWIPE_RUBBER : accumulated;
                    setSwipeOffset(offset);
                }
            };

            const onUp = (ev: MouseEvent) => {
                document.removeEventListener("mousemove", onMove);
                document.removeEventListener("mouseup", onUp);

                if (scaleRef.current <= 1 && hasMultiple) {
                    const totalDx = ev.clientX - e.clientX;
                    if (totalDx < -SWIPE_THRESHOLD && currentIndex < imageList.length - 1) {
                        goTo(currentIndex + 1);
                    } else if (totalDx > SWIPE_THRESHOLD && currentIndex > 0) {
                        goTo(currentIndex - 1);
                    } else {
                        setSwipeOffset(0);
                    }
                }

                setTimeout(() => { isDraggingRef.current = false; }, 50);
            };

            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
        };

        wrapper.addEventListener("mousedown", onMouseDown);
        return () => wrapper.removeEventListener("mousedown", onMouseDown);
    }, [open, applyTransform, clampTranslate, currentIndex, hasMultiple, imageList.length, goTo]);

    const handleWrapperClick = () => {
        if (!isDraggingRef.current && scaleRef.current <= 1.05) {
            setBarsVisible(prev => !prev);
        }
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        if (scaleRef.current > 1) {
            scaleRef.current = 1;
            translateRef.current = { x: 0, y: 0 };
        } else {
            const rect = wrapper.getBoundingClientRect();
            scaleRef.current = 1.7;
            translateRef.current = {
                x: (rect.width / 2 - (e.clientX - rect.left)) * (scaleRef.current - 1) / scaleRef.current,
                y: (rect.height / 2 - (e.clientY - rect.top)) * (scaleRef.current - 1) / scaleRef.current,
            };
            clampTranslate();
        }
        applyTransform(true);
    };

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        const a = document.createElement("a");
        a.href = currentSrc;
        a.download = "imagem.jpg";
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.click();
    };

    const handleShare = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!currentSrc || !onShare) return;
        void onShare(currentSrc);
    };

    const handleLike = async () => {
        const url = PACOCA_API_URL + `/posts/${!post?.liked ? "like" : "unlike"}/${post?.id}`;
        if (!isAuthenticated) return navigate("/login");
        const validate = validatePrev(user);
        if (validate) { setMessage(validate, "error", undefined, undefined); return; }

        const newLikedState = !isLiked;
        const newTotalLikes = newLikedState ? likes + 1 : Math.max(likes - 1, 0);

        try {
            if (setPost)
                setPost(prev => !prev ? prev : ({ ...prev, liked: newLikedState, total_likes: newTotalLikes }));
            if (setPosts)
                setPosts(prev => !prev ? prev :
                    prev.map(p => p.id === post?.id ? { ...p, liked: newLikedState, total_likes: newTotalLikes } : p)
                );
            await axios.post(url, null, {
                headers: { "Content-Type": "application/json", "Accept": "application/json", "Authorization": `Bearer ${token}` }
            });
        } catch (err: any) {
            const messageError = errorMessage(err);
            if ([401, 403, 429].includes(err?.response?.status)) {
                const reason = err?.response?.data.reason && err?.response?.status == 403 ? err?.response?.data.reason : null;
                setMessage(messageError || "Erro ao enviar o aviso.", "error", undefined, undefined, reason);
            }
        }
    };

    if (!open) return null;

    const isZoomed = scale > 1.05;
    const isSwiping = swipeOffset !== 0;

    // imagens vizinhas para renderizar junto (prev e next) para o slide-in ser visível
    const prevSrc = currentIndex > 0 ? getSrc(currentIndex - 1) : null;
    const nextSrc = currentIndex < imageList.length - 1 ? getSrc(currentIndex + 1) : null;

    return (
        <div className="imgmodal-backdrop" onClick={onClose}>
            <div className="imgmodal-container" onClick={e => e.stopPropagation()}>

                {/* TOP BAR */}
                <div className={`imgmodal-topbar ${barsVisible ? "" : "imgmodal-hidden"}`}>
                    <div className="imgmodal-topbar-left">
                        {username && (
                            <>
                                <img src={getImageUser(img_account)} alt={username} className="imgmodal-avatar" />
                                <div>
                                    <div className="imgmodal-username">@{username}</div>
                                    {timeAgo && <div className="imgmodal-time">{timeAgo}</div>}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="imgmodal-topbar-right">
                        {hasMultiple && (
                            <span className="imgmodal-counter">{currentIndex + 1} / {imageList.length}</span>
                        )}
                        <button className="imgmodal-icon-btn" onClick={handleDownload} aria-label="Baixar imagem" title="Baixar" type="button">
                            <Download size={18} />
                        </button>
                        {onShare && (
                            <button className="imgmodal-icon-btn" onClick={handleShare} aria-label="Compartilhar imagem" title="Compartilhar" type="button">
                                <Share2 size={18} />
                            </button>
                        )}
                        <button className="imgmodal-icon-btn" onClick={onClose} aria-label="Fechar" title="Fechar" type="button">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* ZOOM INDICATOR */}
                {isZoomed && <div className="imgmodal-zoom-badge">{scale.toFixed(1)}×</div>}

                {/* IMAGE STRIP + SETAS */}
                <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center", overflow: isZoomed ? "visible" : "hidden" }}>

                    {hasMultiple && !isZoomed && (
                        <button
                            className={`imgmodal-arrow imgmodal-arrow-left ${barsVisible ? "" : "imgmodal-hidden"}`}
                            onClick={goPrev}
                            aria-label="Imagem anterior"
                            type="button"
                            style={{ opacity: currentIndex === 0 ? 0.3 : 1 }}
                        >
                            <ChevronLeft size={28} />
                        </button>
                    )}

                    {/*
                        Strip: 3 slots lado a lado (prev | current | next).
                        Offset base = -100% (current no centro). swipeOffset desloca em px.
                    */}
                    <div
                        ref={wrapperRef}
                        className={`imgmodal-img-wrapper ${isZoomed ? "imgmodal-zoomed" : ""}`}
                        onClick={handleWrapperClick}
                        onDoubleClick={handleDoubleClick}
                        style={{ position: "relative" }}
                    >
                        {hasMultiple ? (
                            <div
                                ref={stripRef}
                                style={{
                                    display: "flex",
                                    width: "300%",
                                    height: "100%",
                                    transform: `translateX(calc(-100% + ${swipeOffset}px))`,
                                    transition: isSwiping ? "none" : "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                    willChange: "transform",
                                    userSelect: "none",
                                }}
                            >
                                {/* Slot PREV */}
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {prevSrc ? (
                                        <img
                                            src={prevSrc}
                                            alt=""
                                            className="imgmodal-img"
                                            draggable={false}
                                            onError={e => (e.currentTarget.src = "/img/image_not_found.png?v=1")}
                                        />
                                    ) : null}
                                </div>
                                {/* Slot CURRENT */}
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <img
                                        ref={imgRef}
                                        src={currentSrc}
                                        alt={alt}
                                        className="imgmodal-img"
                                        draggable={false}
                                        onError={e => (e.currentTarget.src = "/img/image_not_found.png?v=1")}
                                    />
                                </div>
                                {/* Slot NEXT */}
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {nextSrc ? (
                                        <img
                                            src={nextSrc}
                                            alt=""
                                            className="imgmodal-img"
                                            draggable={false}
                                            onError={e => (e.currentTarget.src = "/img/image_not_found.png?v=1")}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <img
                                ref={imgRef}
                                src={currentSrc}
                                alt={alt}
                                className="imgmodal-img"
                                draggable={false}
                                onError={e => (e.currentTarget.src = "/img/image_not_found.png?v=1")}
                            />
                        )}
                    </div>

                    {hasMultiple && !isZoomed && (
                        <button
                            className={`imgmodal-arrow imgmodal-arrow-right ${barsVisible ? "" : "imgmodal-hidden"}`}
                            onClick={goNext}
                            aria-label="Próxima imagem"
                            type="button"
                            style={{ opacity: currentIndex === imageList.length - 1 ? 0.3 : 1 }}
                        >
                            <ChevronRight size={28} />
                        </button>
                    )}
                </div>

                {/* DOTS */}
                {hasMultiple && (
                    <div className={`imgmodal-dots ${barsVisible ? "" : "imgmodal-hidden"}`}>
                        {imageList.map((_, i) => (
                            <button
                                key={i}
                                className={`imgmodal-dot ${i === currentIndex ? "imgmodal-dot-active" : ""}`}
                                onClick={e => { e.stopPropagation(); goTo(i); }}
                                aria-label={`Ir para imagem ${i + 1}`}
                                type="button"
                            />
                        ))}
                    </div>
                )}

                {/* BOTTOM BAR */}
                <div className={`imgmodal-bottombar ${barsVisible ? "" : "imgmodal-hidden"}`}>
                    <div className="imgmodal-reactions">
                        <div className="text-image">
                            <p className="text-image">{post?.text}</p>
                        </div>
                        <div className="imgmodal-reactions-left">
                            {onLike && (
                                <button
                                    className={`imgmodal-react-btn ${isLiked ? "imgmodal-react-active" : ""}`}
                                    onClick={e => { e.stopPropagation(); handleLike(); }}
                                    type="button"
                                    aria-label="Curtir"
                                >
                                    <Heart size={16} fill={isLiked ? "currentColor" : "none"} />
                                    <span>{likes}</span>
                                </button>
                            )}
                            {onComment && (
                                <button
                                    className="imgmodal-react-btn"
                                    onClick={e => { e.stopPropagation(); onComment(); }}
                                    type="button"
                                    aria-label="Comentários"
                                >
                                    <MessageCircle size={16} />
                                    <span>{comments}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageModal;